import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { AiOutlineArrowDown } from "react-icons/ai";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import MultiSelect from "../../../components/form/select/MultiSelect";
import PageTitle from "../../../components/partials/PageTitle";
import { getAllActiveRoles } from "../../../redux/slice/roles/roleSlice";
import {
  getSingleWorkflow,
  resetSingleWorkflow,
  resetWorkflowStatus,
  submitWorkflow,
  updateSingleWorkflow,
} from "../../../redux/slice/workflow/workflowSlice";
import Loading from "./../../../components/loader/Loading";
import { LoaderTwo } from "../../../components/loader/LoaderTwo";

const ViewWorkflow = () => {
  const { active_roles } = useSelector((state) => state.role);
  const { singleWorkflow, isUpdated, isLoading } = useSelector(
    (state) => state.workflow
  );
  const { user_permissions } = useSelector((state) => state?.auth?.user);
  const [action, setAction] = useState("View");
  const [hasEditWorkflow, setHasEditWorkflow] = useState()
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { id } = useParams();
  const targetRoles =
    singleWorkflow &&
    JSON.parse(singleWorkflow?.workflow?.target_role).map((item) => ({
      value: item.role_name,
      label: item.role_name,
    }));

  const breadcrumb = {
    from: {
      title: "workflow",
      link: "/admin/workflow",
    },
    current: {
      title: "view",
      link: null,
    },
  };

  const onSubmit = (values) => {
    let targetResorted = values?.target_roles.map((role) => ({
      role_name: role.value,
    }));
    let data = {
      work_flow_name: values.workflow_name,
      target_roles: targetResorted,
    };
    dispatch(updateSingleWorkflow({ id, data }));
  };

  const {
    values,
    errors,
    touched,
    handleBlur,
    handleChange,
    handleSubmit,
    resetForm,
    getFieldProps,
    setFieldValue,
  } = useFormik({
    enableReinitialize: true,
    initialValues: {
      target_roles: targetRoles,
      workflow_name: singleWorkflow?.workflow?.work_flow_name,
    },
    validate: (values) => {
      const errors = {};
      if (values.target_roles.length < 2) {
        errors.target_roles = "At least two target role is required";
      }
      if (!values?.workflow_name) {
        errors.workflow_name = "Workflow name is required";
      }
      return errors;
    },
    onSubmit,
  });

  const [dragTargetRoles, setDragTargetRoles] = useState(values?.target_roles);

  // Function to handle the drag and rearrange
  const handleOnDragEnd = (result) => {
    if (!result.destination) return; // Item was dropped outside the list

    const items = Array.from(dragTargetRoles);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    setDragTargetRoles(items);
    setFieldValue("target_roles", items);
  };

  useEffect(() => {
    dispatch(getSingleWorkflow(id));
    dispatch(getAllActiveRoles());
  }, []);

  useEffect(() => {
    if (isUpdated) {
      navigate("/admin/workflow", { replace: true });
      dispatch(resetWorkflowStatus());
    }
  }, [isUpdated]);

  useEffect(() => {
    setDragTargetRoles(values?.target_roles);
  }, [values.target_roles]);

  useEffect(() => {
    if (!user_permissions?.some(permission => permission.name === 'workflow module') || !user_permissions?.some(permission => permission.name === 'view workflow')) {
      navigate('/admin/dashboard')
    }

    setHasEditWorkflow(user_permissions?.some(permission => permission.name === 'edit workflow'))
  }, [navigate, user_permissions])

  if (!singleWorkflow) {
    return (
      <div className='mt-3'>
        <LoaderTwo />
      </div>
    );
  }

  return (
    <>
      {/* page title */}
      <PageTitle title={`${action} workflow`} breadcrumb={breadcrumb} />
      {/* end page title */}
      <div className='row'>
        <div className='col-md-12'>
          <div className='card '>
            <div className='card-body'>
              <div className='border border-1 rounded-1 p-2'>
                <form
                  className='workflow-form mt-3 ms-4 row'
                  onSubmit={handleSubmit}
                >
                  <div className='col-md-6'>
                    <div className='mb-3'>
                      <label htmlFor='workflow-name'>Workflow Name</label>
                      <input
                        type='text'
                        id='workflow-name'
                        name='workflow_name'
                        value={values.workflow_name}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className={`form-control form-control-sm mt-1 ${
                          errors?.workflow_name &&
                          touched?.workflow_name &&
                          "is-invalid"
                        }`}
                        placeholder='Enter name'
                      />
                      {errors?.workflow_name && touched?.workflow_name && (
                        <span className='text-danger'>
                          {errors.workflow_name}
                        </span>
                      )}
                    </div>
                    <div className='mb-3'>
                      <label htmlFor='target-role'>Target Role</label>
                      <MultiSelect
                        isError={errors?.target_roles && touched?.target_roles}
                        name='target_roles'
                        id='target-role'
                        field={{ ...getFieldProps("target_roles") }}
                        data={active_roles?.roles?.map((item, i) => ({
                          label: item?.name,
                          value: item?.name,
                        }))}
                        defaultValue={JSON.parse(
                          singleWorkflow?.workflow?.target_role
                        ).map((item) => ({
                          value: item.role_name,
                          label: item.role_name,
                        }))}
                        onChange={setFieldValue}
                        onBlur={() => {
                          handleBlur({ target: { name: "target_roles" } });
                        }}
                        errors={errors}
                        touched={touched}
                      />
                    </div>
                    <DragDropContext onDragEnd={handleOnDragEnd}>
                      <Droppable droppableId='droppable'>
                        {(provided) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.droppableProps}
                            className='mt-3'
                            style={{ width: "fit-content" }}
                          >
                            {dragTargetRoles &&
                              dragTargetRoles?.map((item, i) => (
                                <Draggable
                                  key={i}
                                  draggableId={item.value}
                                  index={i}
                                >
                                  {(provided) => (
                                    <div
                                      ref={provided.innerRef}
                                      {...provided.draggableProps}
                                      {...provided.dragHandleProps}
                                      key={item.value}
                                      className='d-flex justify-content-between align-items-center py-1 px-1'
                                    >
                                      <div className=''>
                                        <p
                                          className=' m-0 px-2 bg-info text-white text-capitalize shadow-hover'
                                          style={{
                                            width: "fit-content",
                                            paddingTop: "3px",
                                            paddingBottom: "3px",
                                          }}
                                        >
                                          {item.label}
                                        </p>
                                        {dragTargetRoles?.length !== i + 1 && (
                                          <AiOutlineArrowDown
                                            size={22}
                                            className='mt-1 text-center'
                                          />
                                        )}
                                      </div>

                                      <div className='d-flex align-items-center gap-2'>
                                        <span
                                          data-bs-toggle='modal'
                                          data-bs-target='#add_clause'
                                        >
                                          {/* <MdEdit onClick={() => setEditData(item)} /> */}
                                        </span>
                                        {/* <span>
                    <MdRemoveRedEye className='text-primary' />
                  </span> */}
                                        {/* <span>
                    <MdClear
                      className='text-danger'
                      onClick={() => dispatch(deleteClause(item?.clause_id))}
                    />
                  </span> */}
                                      </div>
                                    </div>
                                  )}
                                </Draggable>
                              ))}
                            {provided.placeholder}{" "}
                            {/* This should be placed here */}
                          </div>
                        )}
                      </Droppable>
                    </DragDropContext>
                  </div>
                  {hasEditWorkflow && <div className='my-4 '>
                    <button
                      type='submit'
                      className='btn btn-sm btn-primary-1 text-white'
                      disabled={
                        !values.workflow_name ||
                        values.target_roles?.length < 2 ||
                        isLoading
                      }
                    >
                      Update
                    </button>
                  </div>}
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ViewWorkflow;
